// frontend/src/pages/auth/RegisterPage.js
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CssBaseline,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationCity as AddressIcon,
  AccessTime as TimezoneIcon,
  ArrowForward as ForwardIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Import your logo
import syncroLogo from '../../assets/syncro_logo_color.svg';

const GradientBackground = styled('div')({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #f0f4f8 0%, #e6ebee 100%)',
  padding: 16,
});

const StyledPaper = styled(Paper)({
  padding: 32,
  borderRadius: 20,
  maxWidth: 480,
  width: '100%',
  boxShadow: '0 12px 32px rgba(31, 38, 135, 0.12)',
  backdropFilter: 'blur(4px)',
  border: '1px solid rgba(255, 255, 255, 0.7)',
});

// All valid IANA timezones
const timezones = Intl.supportedValuesOf('timeZone');

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    email: '',
    phone: '',
    address: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Default auto timezone
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const result = await register(formData);

    if (result.success) {
      setSuccess(
        result.message ||
          'Registration successful! Please check your email for verification.'
      );
      setTimeout(() => navigate('/login'), 3000);
    } else {
      setError(result.error?.email?.[0] || result.error || 'Registration failed');
    }

    setLoading(false);
  };

  return (
    <GradientBackground>
      <CssBaseline />
      <StyledPaper elevation={0}>
        {/* Header */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Box
            component="img"
            src={syncroLogo}
            alt="Syncro Logo"
            sx={{
              height: 72,
              objectFit: 'contain'
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Register Your Company
          </Typography>
        </Box>

        {/* Error / Success Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            {success}
          </Alert>
        )}

        {/* Form */}
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            label="Company Name"
            name="company_name"
            value={formData.company_name}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1.5 }}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            type="email"
            label="Email Address"
            name="email"
            value={formData.email}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1.5 }}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            label="Phone Number"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1.5 }}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            multiline
            rows={3}
            label="Company Address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AddressIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1.5 }}
          />

          {/* Timezone Dropdown */}
          <TextField
            margin="normal"
            fullWidth
            select
            required
            label="Select Timezone"
            name="timezone"
            value={formData.timezone}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <TimezoneIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1.5 }}
          >
            {timezones.map((tz) => (
              <MenuItem key={tz} value={tz}>
                {tz}
              </MenuItem>
            ))}
          </TextField>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 2,
              mb: 2,
              py: 1.5,
              background: 'linear-gradient(135deg, #4361ee, #3a0ca3)',
              color: 'white',
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '16px',
              borderRadius: '10px',
              boxShadow: '0 4px 16px rgba(67, 97, 238, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #3a50e2, #2d0a9a)',
                boxShadow: '0 6px 20px rgba(67, 97, 238, 0.4)',
              },
              '&:disabled': {
                background: 'linear-gradient(135deg, #a0aec0, #718096)',
                boxShadow: 'none',
              }
            }}
            disabled={loading}
            endIcon={<ForwardIcon />}
          >
            {loading ? 'Registering...' : 'Register Company'}
          </Button>

          <Box sx={{ textAlign: 'center', mt: 1 }}>
            <Link 
              to="/login" 
              style={{ 
                color: '#4361ee', 
                textDecoration: 'none',
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              Already have an account? Sign In
            </Link>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ 
          textAlign: 'center', 
          mt: 4, 
          pt: 2, 
          borderTop: '1px solid #e2e8f0',
          color: '#718096',
          fontSize: '0.8rem'
        }}>
          <Typography variant="body2">
            © {new Date().getFullYear()} Syncro. All rights reserved.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Designed & Developed by{' '}
            <a
              href="https://www.softsolutionmedia.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#4361ee', textDecoration: 'none' }}
            >
              Soft Solution Media
            </a>
          </Typography>
        </Box>
      </StyledPaper>
    </GradientBackground>
  );
};

export default RegisterPage;